/**
 * Workspace search index.
 *
 * Flattens the whole model — nested modules, their interfaces and surfaces,
 * processes, subprocesses and declared types — into one ranked,
 * queryable list. The old ⌘F only walked top-level module names, so a
 * deeply nested module or any interface/process was invisible. This indexes
 * every navigable entity and matches a query against its name, its type
 * keyword (`microservice`, `command`, …) and its qualified path, so
 * "command" finds every command interface and "billing" finds everything
 * under a `Billing` space/module.
 *
 * Pure: a function of the model. Navigation `target`s are data — the shell
 * maps each onto the existing select / fly / mode handlers.
 */
import type {
  ArchitectureModel,
  Module,
  StableId,
  Surface,
} from "@archlang/scene/arch-model";
import { flattenInterfaces, moduleKey } from "@archlang/scene/arch-model";
import { processKey, processQualifiedName } from "../processKey.js";
import { typeSelectionKey } from "../typeKey.js";

export type SearchKind =
  | "module"
  | "interface"
  | "surface"
  | "process"
  | "subprocess"
  | "type"
  | "plane";

/**
 * Where picking a hit takes the user. Surfaces fly to their owning module
 * (they aren't separately selectable); subprocesses resolve to the process
 * or module that owns them.
 */
export type SearchTarget =
  | { readonly kind: "module"; readonly moduleKey: StableId }
  | {
      readonly kind: "interface";
      readonly qualifiedName: string;
      readonly moduleKey: StableId;
    }
  | { readonly kind: "process"; readonly processKey: string }
  | { readonly kind: "type"; readonly name: string }
  | { readonly kind: "plane"; readonly plane: string };

export interface SearchEntry {
  /** Stable, unique per entity — used as the React key. */
  readonly id: string;
  readonly kind: SearchKind;
  /** Primary display name (matched strongest). */
  readonly label: string;
  /** Type keyword (microservice / command / query / …), when it has one. */
  readonly typeName?: string;
  /** Dotted breadcrumb of the owning context (excludes the label itself). */
  readonly path: string;
  readonly target: SearchTarget;
}

/** Flatten every navigable entity in the model into a flat search list. */
export function buildSearchEntries(
  model: ArchitectureModel | null,
): readonly SearchEntry[] {
  if (!model) return [];
  const entries: SearchEntry[] = [];
  // Qualified module path → its fly/select key, so subprocesses owned by a
  // module can resolve a navigation target.
  const moduleKeyByQual = new Map<string, StableId>();
  // v0.9.0 §1.1: distinct non-primary planes → searchable so ⌘F can activate the
  // plane switcher on a plane by name.
  const planes = new Set<string>();

  const visitSurface = (s: Surface, parentQual: string, ownerKey: StableId) => {
    const sQual = `${parentQual}.${s.name}`;
    entries.push({
      id: `surface:${ownerKey}:${sQual}`,
      kind: "surface",
      label: s.name,
      typeName: s.typeName,
      path: parentQual,
      // Surfaces aren't separately selectable — land on the owning module.
      target: { kind: "module", moduleKey: ownerKey },
    });
    for (const child of s.surfaces) visitSurface(child, sQual, ownerKey);
  };

  const visit = (m: Module, parentPath: string) => {
    const key = moduleKey(m) as StableId;
    const qual = parentPath ? `${parentPath}.${m.name}` : m.name;
    moduleKeyByQual.set(qual, key);
    // Space-qualified alias (v0.11 D5): two spaces may each own the SAME bare
    // qualified path — a space-aware owner lookup must not last-wins onto one.
    if (m.space) moduleKeyByQual.set(`${m.space}::${qual}`, key);
    // D5 breadcrumb: display names stay bare; the PATH carries the landed
    // space so twin rows across spaces are tellable-apart (and "billing"
    // matches everything under a Billing space, per this index's contract).
    const crumb = m.space ? (parentPath ? `${m.space}.${parentPath}` : m.space) : parentPath;

    entries.push({
      // Keyed by the STABLE module key, not the qualified path — two
      // top-level modules may share a name across files (the demo has two
      // `Notifications`), and duplicate ids break React list keys.
      id: `module:${key}`,
      kind: "module",
      label: m.name,
      typeName: m.typeName,
      path: crumb,
      target: { kind: "module", moduleKey: key },
    });

    // flattenInterfaces matches the keying useModelIndexes uses, so the
    // qualified name we hand handleSelectInterface resolves in interfaceById.
    flattenInterfaces(m).forEach((iface, i) => {
      // Address by the owner's STABLE-KEY form (`${moduleKey}.${name}`) — the
      // same form the nav tree and board ports use. useModelIndexes aliases it
      // onto the interface record, and unlike the bare name path it stays
      // unique when two spaces (or a `#id`) own same-named modules — a bare
      // `Api.handle` pick last-wins onto ONE twin's inspector (v0.11 P4).
      const qn = `${key}.${iface.name}`;
      entries.push({
        // Prefix the owner's stable key AND the flatten position: two
        // interfaces sharing a name across the module's surfaces (e.g. an
        // http `read` and a grpc `read`) flatten under the same key, so the
        // name alone is not unique — the index keeps the React key distinct.
        id: `interface:${key}:${i}:${iface.name}`,
        kind: "interface",
        label: iface.name,
        typeName: iface.typeName,
        path: m.space ? `${m.space}.${qual}` : qual,
        target: { kind: "interface", qualifiedName: qn, moduleKey: key },
      });
    });

    for (const p of m.planes ?? []) planes.add(p);
    for (const s of m.surfaces) visitSurface(s, qual, key);
    for (const c of m.children) visit(c, qual);
  };
  for (const m of model.modules) visit(m, "");

  for (const plane of [...planes].sort()) {
    entries.push({
      id: `plane:${plane}`,
      kind: "plane",
      label: plane,
      path: "plane",
      target: { kind: "plane", plane },
    });
  }

  model.processes.forEach((p, i) => {
    entries.push({
      // Position-prefixed so the React list key is unique even for two
      // byte-identical anonymous processes (which share one content `processKey`)
      // — matches how the interface/surface entries are keyed.
      id: `process:${i}:${processKey(p)}`,
      kind: "process",
      // Anonymous processes have an empty name — show a placeholder so the row
      // isn't blank.
      label: p.name || "(anonymous)",
      // D5 breadcrumb: space-prefixed owner chain (bare when spaceless).
      path: [p.space, ...(p.ownerPath ?? [])].filter(Boolean).join("."),
      target: { kind: "process", processKey: processKey(p) },
    });
  });

  // A subprocess can be owned by a process or a module — resolve to whichever
  // its ownerPath names. Free-standing/unresolvable subprocesses have no
  // board home, so they're skipped rather than yielding a dead pick.
  //
  // Owner matching is SPACE-AWARE (v0.11 P4): the pick must carry the owning
  // process's REAL key (`processKey`, space-qualified for a spaced owner) —
  // a bare `Checkout` dereferences to nothing in the space-keyed processById,
  // and with twin owners across spaces it would land on the wrong one.
  const owningProcess = (owner: string, space?: string) => {
    const cands = model.processes.filter(
      (p) => processQualifiedName(p) === owner || processKey(p) === owner,
    );
    return cands.find((p) => (p.space ?? "") === (space ?? "")) ?? cands[0] ?? null;
  };
  for (const sp of model.subprocesses) {
    const owner =
      sp.ownerPath && sp.ownerPath.length > 0 ? sp.ownerPath.join(".") : "";
    let target: SearchTarget | null = null;
    const ownerProc = owner ? owningProcess(owner, sp.space) : null;
    if (ownerProc) {
      target = { kind: "process", processKey: processKey(ownerProc) };
    } else if (owner) {
      const mKey =
        (sp.space ? moduleKeyByQual.get(`${sp.space}::${owner}`) : undefined) ??
        moduleKeyByQual.get(owner);
      if (mKey) target = { kind: "module", moduleKey: mKey };
    }
    if (!target) continue;
    entries.push({
      // The landed space keeps twin `Owner.Name` ids distinct across spaces.
      id: `subprocess:${sp.space ? `${sp.space}::` : ""}${owner}.${sp.name}`,
      kind: "subprocess",
      label: sp.name,
      path: [sp.space, owner].filter(Boolean).join("."),
      target,
    });
  }

  // Types are space-scoped entities — two spaces may each declare the same
  // bare name — so both the id (the React key) and the pick target carry the
  // space-qualified selection key (r56).
  for (const t of model.types) {
    entries.push({
      id: `type:${t.space ?? t.library ?? ""}:${t.name}`,
      kind: "type",
      label: t.name,
      typeName: t.parentType,
      path: t.space ?? t.library ?? "",
      target: { kind: "type", name: typeSelectionKey(t) },
    });
  }

  return entries;
}

// Tie-breaker so a name hit outranks a same-strength type/path hit, and
// modules float above their own interfaces when scores are otherwise equal.
const KIND_BIAS: Record<SearchKind, number> = {
  module: 6,
  interface: 5,
  process: 4,
  surface: 3,
  subprocess: 2,
  type: 1,
  plane: 1,
};

/** Score one field: exact > prefix > substring; shorter haystacks win ties. */
function fieldScore(haystack: string, needle: string): number {
  const h = haystack.toLowerCase();
  if (h === needle) return 100;
  const lenBonus = (1 - Math.min(h.length, 200) / 200) * 10;
  if (h.startsWith(needle)) return 70 + lenBonus;
  const i = h.indexOf(needle);
  if (i < 0) return -1;
  return 40 - Math.min(i, 40) + lenBonus * 0.5;
}

/**
 * Match a query against an entry's name, type keyword and path. Name hits
 * dominate (+200), type hits sit in the middle (+80), path hits last (+40).
 * Empty query ranks by kind alone (a useful default list). Returns -1 for a
 * non-empty query with no field match.
 */
export function scoreEntry(entry: SearchEntry, query: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return KIND_BIAS[entry.kind];
  const name = fieldScore(entry.label, q);
  const type = entry.typeName ? fieldScore(entry.typeName, q) : -1;
  const path = entry.path ? fieldScore(entry.path, q) : -1;
  let best = -1;
  if (name >= 0) best = Math.max(best, name + 200);
  if (type >= 0) best = Math.max(best, type + 80);
  if (path >= 0) best = Math.max(best, path + 40);
  if (best < 0) return -1;
  return best + KIND_BIAS[entry.kind];
}

/** Filter + sort entries for a query, capped at `limit`. */
export function rankEntries(
  entries: readonly SearchEntry[],
  query: string,
  limit = 40,
): readonly SearchEntry[] {
  const hasQuery = query.trim().length > 0;
  const scored: Array<{ entry: SearchEntry; score: number }> = [];
  for (const entry of entries) {
    const score = scoreEntry(entry, query);
    if (score < 0 && hasQuery) continue;
    scored.push({ entry, score });
  }
  scored.sort(
    (a, b) =>
      b.score - a.score ||
      a.entry.label.localeCompare(b.entry.label) ||
      a.entry.path.localeCompare(b.entry.path),
  );
  return scored.slice(0, limit).map((s) => s.entry);
}
